// M6.7 — Admin Flashcard Settings (simplified 2026-04-24):
// Chỉ còn 3 field daily:
//   daily_new_quota    — thẻ mới TỐI ĐA/ngày
//   daily_min_target   — thẻ mới TỐI THIỂU/ngày (để banner "Hoàn thành nhiệm vụ")
//   daily_review_min   — thẻ ôn TỐI THIỂU/ngày (không có max — ôn càng nhiều càng tốt)
//
// Luật (đồng bộ với Zod .refine() BE):
//   daily_min_target ≤ daily_new_quota
//
// Backend: GET/PUT /admin/flashcard/settings — admin login only (không TOTP).
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../lib/api.js';

const FIELDS = [
  { key: 'daily_new_quota',   label: 'Thẻ mới TỐI ĐA / ngày',
    hint: 'Tổng số thẻ mới tối đa mỗi NV được học trong 1 ngày. Vượt quá sẽ dừng phục vụ thẻ mới.',
    min: 1, max: 200,
    color: 'brand' },
  { key: 'daily_min_target',  label: 'Thẻ mới TỐI THIỂU / ngày',
    hint: 'NV cần học bao nhiêu thẻ mới để "Hoàn thành nhiệm vụ hôm nay". Phải ≤ tối đa.',
    min: 1, max: 200,
    color: 'brand' },
  { key: 'daily_review_min',  label: 'Thẻ ôn TỐI THIỂU / ngày',
    hint: 'NV phải ôn ít nhất bao nhiêu thẻ tới hạn trong ngày. KHÔNG có giới hạn tối đa — tới hạn thì phải ôn, ôn càng nhiều càng tốt.',
    min: 0, max: 500,
    color: 'amber' },
];

const DEFAULT_VALUES = {
  daily_new_quota: 30,
  daily_min_target: 10,
  daily_review_min: 10,
  review_group_by_topic: false,
};

export default function AdminFlashcardPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const [values, setValues] = useState(DEFAULT_VALUES);
  const [current, setCurrent] = useState(DEFAULT_VALUES);
  const [defaults, setDefaults] = useState(DEFAULT_VALUES);

  async function load() {
    setLoading(true);
    setErr('');
    try {
      const res = await api.adminFlashcardGetSettings();
      const next = {};
      for (const f of FIELDS) next[f.key] = res[f.key];
      // M39: read boolean field riêng
      next.review_group_by_topic = !!res.review_group_by_topic;
      setValues(next);
      setCurrent(next);
      setDefaults(res.defaults || DEFAULT_VALUES);
    } catch (e) {
      setErr(e.message || 'Không tải được cấu hình');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  // ---- Validation ----
  const intsOk = FIELDS.every(f => {
    const v = values[f.key];
    return Number.isInteger(v) && v >= f.min && v <= f.max;
  });
  const dailyOk = values.daily_min_target <= values.daily_new_quota;
  const allRefines = dailyOk;
  const intFieldsChanged = FIELDS.some(f => values[f.key] !== current[f.key]);
  const boolChanged = values.review_group_by_topic !== current.review_group_by_topic;
  const changed = intFieldsChanged || boolChanged;
  const canSave = intsOk && allRefines && !saving && changed;

  async function onSave() {
    if (!canSave) return;
    setSaving(true);
    setErr('');
    try {
      const payload = {};
      for (const f of FIELDS) payload[f.key] = values[f.key];
      payload.review_group_by_topic = !!values.review_group_by_topic;
      const res = await api.adminFlashcardUpdateSettings(payload);
      const next = {};
      for (const f of FIELDS) next[f.key] = res[f.key];
      next.review_group_by_topic = !!res.review_group_by_topic;
      setCurrent(next);
      toast.success('Đã lưu cấu hình flashcard');
    } catch (e) {
      setErr(e.message || 'Lưu thất bại');
      toast.error(e.message || 'Lưu thất bại');
    } finally {
      setSaving(false);
    }
  }

  function resetToDefault() {
    setValues({ ...defaults });
  }

  function setField(key, v) {
    setValues(prev => ({ ...prev, [key]: v }));
  }

  return (
    <div className="min-h-full p-4 md:p-6">
      <div className="max-w-3xl mx-auto">
        <Link to="/admin" className="text-sm text-slate-500 hover:text-slate-700">← Trang admin</Link>
        <header className="mt-2 mb-6">
          <h1 className="text-2xl font-semibold text-brand-700">Cấu hình Flashcard</h1>
          <p className="text-sm text-slate-500 mt-1">
            Áp dụng toàn hệ thống · giới hạn thẻ mới & thẻ ôn mỗi ngày
          </p>
        </header>

        {loading ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center text-slate-500">
            Đang tải cấu hình…
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            {err && (
              <div className="mb-4 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-sm text-rose-700">
                {err}
              </div>
            )}

            {/* Section 1: Thẻ mới */}
            <section className="mb-6">
              <h2 className="text-sm font-semibold text-brand-700 uppercase tracking-wide mb-1">
                📘 Thẻ mới — học kiến thức lần đầu
              </h2>
              <p className="text-xs text-slate-500 mb-3">
                Áp dụng cộng dồn cả ngày (reset 00:00).
              </p>
              <div className="space-y-4">
                {FIELDS.filter(f => f.key.startsWith('daily_new') || f.key === 'daily_min_target').map(f => (
                  <FieldRow
                    key={f.key}
                    field={f}
                    value={values[f.key]}
                    onChange={(v) => setField(f.key, v)}
                    defaultValue={defaults[f.key]}
                  />
                ))}
              </div>
            </section>

            {/* Section 2: Thẻ ôn */}
            <section className="mb-6 pt-4 border-t border-slate-100">
              <h2 className="text-sm font-semibold text-amber-700 uppercase tracking-wide mb-1">
                🔁 Thẻ ôn — nhớ lại theo SM-2
              </h2>
              <p className="text-xs text-slate-500 mb-3">
                Thẻ tới hạn là bắt buộc phải ôn. Không giới hạn tối đa — ôn càng nhiều, nhớ càng lâu.
              </p>
              <div className="space-y-4">
                {FIELDS.filter(f => f.key === 'daily_review_min').map(f => (
                  <FieldRow
                    key={f.key}
                    field={f}
                    value={values[f.key]}
                    onChange={(v) => setField(f.key, v)}
                    defaultValue={defaults[f.key]}
                  />
                ))}
              </div>
            </section>

            {/* Section 3 (M39): Chế độ nâng cao — group by topic */}
            <section className="mb-6 pt-4 border-t border-slate-100">
              <h2 className="text-sm font-semibold text-indigo-700 uppercase tracking-wide mb-1">
                🧩 Chế độ nâng cao
              </h2>
              <p className="text-xs text-slate-500 mb-3">
                Tinh chỉnh thứ tự phục vụ thẻ khi nhân viên ôn.
              </p>
              <ToggleRow
                label="Gom thẻ cùng chủ đề"
                hint="Khi bật: thẻ đến hạn ôn cùng 1 chủ đề sẽ hiện liên tiếp, nhân viên ôn sâu 1 chủ đề rồi mới qua chủ đề khác. Khi tắt (mặc định): thẻ hiện theo thứ tự đến hạn sớm nhất (SM-2 chuẩn)."
                value={values.review_group_by_topic}
                onChange={(v) => setField('review_group_by_topic', v)}
                isDefault={values.review_group_by_topic === defaults.review_group_by_topic}
                defaultValue={defaults.review_group_by_topic}
              />
            </section>

            {/* Validation warnings */}
            <div className="space-y-1 mb-4">
              {!intsOk && (
                <div className="text-sm text-rose-600">Giá trị phải là số nguyên trong phạm vi cho phép.</div>
              )}
              {intsOk && !dailyOk && (
                <ValidationWarn msg={`Thẻ mới tối thiểu (${values.daily_min_target}) đang > tối đa (${values.daily_new_quota}).`} />
              )}
            </div>

            {/* Preview */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs text-slate-600 space-y-3">
              <div className="font-semibold text-slate-800 text-sm mb-1">
                👤 NV thấy gì với cấu hình hiện tại:
              </div>
              <div className="space-y-1">
                <div>
                  • Banner "Nhiệm vụ hôm nay: học <b className="text-brand-700">{values.daily_min_target}</b> thẻ mới + ôn <b className="text-amber-700">{values.daily_review_min}</b> thẻ tới hạn"
                </div>
                <div>
                  • Chạm <b>{values.daily_new_quota}</b> thẻ mới trong ngày → dừng phục vụ thẻ mới (đã đủ)
                </div>
                <div>
                  • Thẻ tới hạn: hiện hết lên, NV ôn bao nhiêu tùy ý (không bị chặn ở số nào)
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="mt-6 flex items-center justify-between border-t border-slate-200 pt-4">
              <button
                onClick={resetToDefault}
                className="text-sm text-slate-500 hover:text-slate-700"
                type="button"
              >
                ↺ Khôi phục mặc định
              </button>
              <div className="flex gap-2">
                <button
                  onClick={load}
                  className="px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  disabled={saving}
                  type="button"
                >
                  Tải lại
                </button>
                <button
                  onClick={onSave}
                  className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!canSave}
                  type="button"
                >
                  {saving ? 'Đang lưu…' : 'Lưu cấu hình'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 2026-04-24: SM-2 Parameters section */}
        <Sm2ConfigSection />
      </div>
    </div>
  );
}

// ============================================================
// 2026-04-24: SM-2 Parameters — admin điều chỉnh thuật toán lặp lại
// ============================================================
function Sm2ConfigSection() {
  // M103 (2026-04-26): thêm 3 setting quality-aware
  const FALLBACK = { ef_default: 2.5, ef_max: 3.0, interval_level_2: 6, learn_again_minutes: 10, interval_level_easy: 7, hard_mult: 1.2, easy_mult: 1.3 };
  const [cfg, setCfg] = useState(FALLBACK);
  const [defaults, setDefaults] = useState(FALLBACK);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await api.adminSm2Config();
      setCfg({
        ef_default: Number(r.ef_default) || FALLBACK.ef_default,
        ef_max: Number(r.ef_max) || FALLBACK.ef_max,
        interval_level_2: Number(r.interval_level_2) || FALLBACK.interval_level_2,
        learn_again_minutes: Number(r.learn_again_minutes) || FALLBACK.learn_again_minutes,
        // M103
        interval_level_easy: Number(r.interval_level_easy) || FALLBACK.interval_level_easy,
        hard_mult: Number(r.hard_mult) || FALLBACK.hard_mult,
        easy_mult: Number(r.easy_mult) || FALLBACK.easy_mult,
      });
      if (r.defaults) {
        setDefaults({
          ef_default: Number(r.defaults.ef_default) || FALLBACK.ef_default,
          ef_max: Number(r.defaults.ef_max) || FALLBACK.ef_max,
          interval_level_2: Number(r.defaults.interval_level_2) || FALLBACK.interval_level_2,
          learn_again_minutes: Number(r.defaults.learn_again_minutes) || FALLBACK.learn_again_minutes,
          // M103
          interval_level_easy: Number(r.defaults.interval_level_easy) || FALLBACK.interval_level_easy,
          hard_mult: Number(r.defaults.hard_mult) || FALLBACK.hard_mult,
          easy_mult: Number(r.defaults.easy_mult) || FALLBACK.easy_mult,
        });
      }
    } catch (e) {
      toast.error('Lỗi load SM-2 config: ' + (e?.data?.error || e?.message));
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function onSave() {
    if (!cfg || saving) return;
    setSaving(true);
    try {
      await api.adminSm2ConfigUpdate(cfg);
      toast.success('Đã lưu SM-2 parameters');
    } catch (e) {
      toast.error('Lưu fail: ' + (e?.data?.error || e?.message));
    } finally { setSaving(false); }
  }

  function resetDefault() {
    if (defaults) setCfg({
      ef_default: defaults.ef_default,
      ef_max: defaults.ef_max,
      interval_level_2: defaults.interval_level_2,
      learn_again_minutes: defaults.learn_again_minutes,
      // M103
      interval_level_easy: defaults.interval_level_easy,
      hard_mult: defaults.hard_mult,
      easy_mult: defaults.easy_mult,
    });
  }

  // Preview interval 6 lần đầu (rate "Ổn" = q3, EF giữ nguyên)
  const preview = (() => {
    if (!cfg) return [];
    const arr = [];
    let ef = cfg.ef_default;
    let interval = 1;
    arr.push(1);                              // lần 1
    arr.push(cfg.interval_level_2);           // lần 2
    interval = cfg.interval_level_2;
    for (let i = 3; i <= 6; i++) {
      interval = Math.max(1, Math.round(interval * ef));
      arr.push(interval);
    }
    return arr;
  })();

  if (loading && !cfg) return (
    <div className="mt-6 bg-white rounded-xl border border-slate-200 p-5 text-sm text-slate-500">
      Đang tải SM-2 config…
    </div>
  );

  return (
    <div className="mt-6 bg-white rounded-xl border border-slate-200 p-5 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-semibold text-slate-800">🧠 Thuật toán SM-2</h3>
          <p className="text-xs text-slate-500 mt-1">
            Điều chỉnh mức độ dãn lịch ôn tập. EF (ease factor) càng cao → interval dãn càng nhanh → NV ôn ít hơn.
          </p>
        </div>
        <button
          onClick={resetDefault}
          className="text-xs px-3 py-1.5 rounded border border-slate-300 text-slate-600 hover:bg-slate-50"
        >
          ↺ Về mặc định
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Sm2Field
          label="Ease Factor mặc định"
          hint="EF ban đầu của thẻ mới. Càng cao → interval dãn nhanh. Mặc định 2.5 (SM-2 gốc)."
          step={0.1} min={1.3} max={3.5}
          value={cfg.ef_default}
          onChange={v => setCfg({ ...cfg, ef_default: v })}
          isDefault={cfg.ef_default === defaults?.ef_default}
        />
        <Sm2Field
          label="Ease Factor tối đa (cap)"
          hint="Giới hạn trần EF. Khi thẻ rate nhiều lần 'Dễ', EF tăng nhưng không vượt mốc này."
          step={0.1} min={1.5} max={5.0}
          value={cfg.ef_max}
          onChange={v => setCfg({ ...cfg, ef_max: v })}
          isDefault={cfg.ef_max === defaults?.ef_max}
        />
        <Sm2Field
          label="Interval lần 2 (ngày)"
          hint="Số ngày trước khi thẻ được ôn lần thứ 2 sau lần đầu 1 ngày. SM-2 gốc = 6."
          step={1} min={1} max={90} isInt
          value={cfg.interval_level_2}
          onChange={v => setCfg({ ...cfg, interval_level_2: v })}
          isDefault={cfg.interval_level_2 === defaults?.interval_level_2}
        />
        <Sm2Field
          label="Học lại (phút) khi Quên"
          hint="Khi NV bấm 'Quên' (q=1), thẻ sẽ xuất hiện lại sau bao lâu (trong cùng session)."
          step={1} min={1} max={120} isInt
          value={cfg.learn_again_minutes}
          onChange={v => setCfg({ ...cfg, learn_again_minutes: v })}
          isDefault={cfg.learn_again_minutes === defaults?.learn_again_minutes}
        />
        {/* M103 (2026-04-26): 3 setting quality-aware */}
        <Sm2Field
          label="Interval lần đầu khi Siêu Dễ (ngày)"
          hint="Khi card mới + NV bấm 'Siêu Dễ' (q=4) lần đầu → schedule sau N ngày. Nên > Interval lần 2 (mặc định 7 vs 6) để skip 1 cấp."
          step={1} min={1} max={90} isInt
          value={cfg.interval_level_easy}
          onChange={v => setCfg({ ...cfg, interval_level_easy: v })}
          isDefault={cfg.interval_level_easy === defaults?.interval_level_easy}
        />
        <Sm2Field
          label="Hệ số Khó (rep ≥ 2)"
          hint="Khi NV bấm 'Khó' (q=2) từ lần ôn thứ 2 trở đi → interval = prevInterval × hệ số. Default 1.2 (giảm tốc nhẹ)."
          step={0.05} min={1.0} max={2.0}
          value={cfg.hard_mult}
          onChange={v => setCfg({ ...cfg, hard_mult: v })}
          isDefault={cfg.hard_mult === defaults?.hard_mult}
        />
        <Sm2Field
          label="Hệ số Siêu Dễ (rep ≥ 2)"
          hint="Khi NV bấm 'Siêu Dễ' (q=4) từ lần ôn thứ 2 → interval = prevInterval × EF × hệ số. Default 1.3 (bonus 30%)."
          step={0.05} min={1.0} max={2.5}
          value={cfg.easy_mult}
          onChange={v => setCfg({ ...cfg, easy_mult: v })}
          isDefault={cfg.easy_mult === defaults?.easy_mult}
        />
      </div>

      {/* Preview */}
      <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 space-y-2">
        <div>
          <div className="text-xs font-semibold text-sky-800 mb-1.5">📊 Preview — interval 6 lần đầu (rate "Dễ" liên tục)</div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {preview.map((n, i) => (
              <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-white border border-sky-200 text-xs">
                <span className="font-mono text-slate-400">L{i + 1}</span>
                <strong className="text-sky-700">{n}</strong>
                <span className="text-slate-500">ngày</span>
              </span>
            ))}
          </div>
          <div className="text-[11px] text-sky-700 mt-2">
            Thẻ sẽ gặp lại sau {preview[0]}d → {preview[1]}d → {preview[2]}d → {preview[3]}d → {preview[4]}d → {preview[5]}d nếu NV giữ rate "Dễ" (q=3).
          </div>
        </div>

        {/* M103: Preview 3 nút lần đầu */}
        <div className="border-t border-sky-200 pt-2">
          <div className="text-xs font-semibold text-sky-800 mb-1.5">🎯 Card MỚI lần đầu — interval khi NV bấm:</div>
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-rose-100 text-rose-800 border border-rose-200">
              ❌ Quên: <strong>{cfg.learn_again_minutes}</strong> phút
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-amber-100 text-amber-800 border border-amber-200">
              😬 Khó: <strong>1</strong> ngày
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-sky-100 text-sky-800 border border-sky-200">
              😄 Dễ: <strong>{cfg.interval_level_2}</strong> ngày
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-100 text-emerald-800 border border-emerald-200">
              👑 Siêu Dễ: <strong>{cfg.interval_level_easy}</strong> ngày
            </span>
          </div>
          <div className="text-[11px] text-sky-700 mt-2">
            Khó / Dễ / Siêu Dễ phải khác nhau để NV thấy ý nghĩa lựa chọn (M103 fix). Recommend: Khó=1 &lt; Dễ={cfg.interval_level_2} &lt; Siêu Dễ={cfg.interval_level_easy}.
          </div>
        </div>
      </div>

      <div className="flex gap-2 justify-end">
        <button
          onClick={load}
          disabled={saving}
          className="px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          Tải lại
        </button>
        <button
          onClick={onSave}
          disabled={saving}
          className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-50"
        >
          {saving ? 'Đang lưu…' : 'Lưu SM-2 config'}
        </button>
      </div>
    </div>
  );
}

function Sm2Field({ label, hint, value, onChange, step, min, max, isInt = false, isDefault = false }) {
  return (
    <div className="border border-slate-200 rounded-lg p-3">
      <label className="flex items-center justify-between text-sm font-medium text-slate-700">
        <span>{label}</span>
        {isDefault && <span className="text-[10px] text-slate-400 font-normal">(default)</span>}
      </label>
      <input
        type="number"
        step={step} min={min} max={max}
        value={value}
        onChange={e => {
          const v = isInt ? parseInt(e.target.value, 10) : parseFloat(e.target.value);
          if (Number.isFinite(v)) onChange(v);
        }}
        className="mt-1 w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
      />
      <p className="text-[11px] text-slate-500 mt-1">{hint}</p>
    </div>
  );
}

// ============================================================
// Subcomponents
// ============================================================

function FieldRow({ field, value, onChange, defaultValue }) {
  const isDefault = value === defaultValue;
  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr,180px] gap-3 items-start">
      <div>
        <label className="block text-sm font-medium text-slate-800">
          {field.label}
          {!isDefault && (
            <span className="ml-2 text-xs text-amber-600">(mặc định: {defaultValue})</span>
          )}
        </label>
        <p className="text-xs text-slate-500 mt-0.5">{field.hint}</p>
      </div>
      <NumberInput
        value={value}
        onChange={onChange}
        min={field.min ?? 1}
        max={field.max ?? 200}
      />
    </div>
  );
}

// Toggle switch cho boolean setting (M39)
function ToggleRow({ label, hint, value, onChange, isDefault, defaultValue }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr,auto] gap-3 items-start">
      <div>
        <label className="block text-sm font-medium text-slate-800">
          {label}
          {!isDefault && (
            <span className="ml-2 text-xs text-amber-600">
              (mặc định: {defaultValue ? 'Bật' : 'Tắt'})
            </span>
          )}
        </label>
        <p className="text-xs text-slate-500 mt-0.5">{hint}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        onClick={() => onChange(!value)}
        className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors shrink-0 ${
          value ? 'bg-indigo-500' : 'bg-slate-300'
        }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${
            value ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
}

function NumberInput({ value, onChange, min = 1, max = 200 }) {
  return (
    <input
      type="number"
      inputMode="numeric"
      min={min}
      max={max}
      step={1}
      value={Number.isFinite(value) ? value : ''}
      onChange={(e) => {
        const v = e.target.value;
        if (v === '') return onChange(NaN);
        const n = parseInt(v, 10);
        onChange(Number.isFinite(n) ? n : NaN);
      }}
      className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm text-right focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
    />
  );
}

function ValidationWarn({ msg }) {
  return (
    <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
      ⚠️ {msg}
    </div>
  );
}
